import type { ClCampaign, ClHeaderStats } from '@elva/ui';
import { growthGet, growthList, growthPatch, growthPost } from '@/data/http';
import { defaultMarketingPeriod } from '@/data/marketingScope';
import type { BeCampaign, BeCampaignSummary } from '@/data/marketingApiTypes';
import { num, stableNumericId, str } from '@/data/adapt';
import {
  campaignAlertTypeToFe,
  campaignHealthToFe,
  campaignLpPhaseToFe,
} from '@/data/growthEnums';

export interface CampaignListData {
  items: ClCampaign[];
  idMap: Record<number, string>;
  headerStats?: ClHeaderStats;
}

const TARGET_LABELS: Record<string, string> = {
  emergency: 'Emergency',
  'general-dentistry': 'General dentistry',
  implants: 'Implants',
  whitening: 'Whitening',
  orthodontics: 'Orthodontics',
};

function centsToDollars(cents: number | null | undefined): number {
  if (cents == null || !Number.isFinite(cents)) return 0;
  return Math.round(cents / 100);
}

function parseRoi(value: string | number | null | undefined): number {
  if (value == null) return 0;
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

function formatCampaignTarget(raw: string | null | undefined): string {
  const key = (raw ?? '').trim();
  if (!key) return '—';
  return TARGET_LABELS[key] ?? key.replace(/-/g, ' ');
}

function formatMomDelta(delta: number | null | undefined, asMoney = false): string | undefined {
  if (delta == null || !Number.isFinite(delta)) return undefined;
  if (asMoney) {
    const sign = delta >= 0 ? '+' : '-';
    return `${sign}$${Math.round(Math.abs(delta) / 100).toLocaleString()} vs last month`;
  }
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)} vs last month`;
}

function tileByKey(tiles: BeCampaignSummary['tiles'], key: string) {
  return tiles?.find((tile) => String(tile.key ?? '').toLowerCase() === key.toLowerCase());
}

function adaptCampaign(row: BeCampaign): { campaign: ClCampaign; backendId: string } {
  const status = (row.status ?? '').toLowerCase();
  const paused = row.paused === true || status === 'paused' || status === 'archived';
  const active = status === 'live';

  const spend =
    row.spend ?? centsToDollars(row.current_month_spend_cents ?? row.spend_cents);
  const budget =
    row.budget ?? centsToDollars(row.monthly_budget_cents ?? row.budget_cents ?? row.daily_budget_cents);
  const revenue = row.revenue ?? centsToDollars(row.revenue_cents);
  const leads = num(row.last_7d_leads ?? row.leads);
  const costPerPatient =
    row.cost_per_patient ??
    centsToDollars(
      row.last_7d_cost_per_lead_cents ??
        row.cost_per_lead_cents ??
        row.cost_per_patient_cents,
    );
  const roi = parseRoi(row.last_7d_roi_pct ?? row.roi);

  const backendId = String(row.id ?? '');
  const numericId = stableNumericId(backendId);

  return {
    backendId,
    campaign: {
      id: numericId,
      name: str(row.name),
      target: formatCampaignTarget(row.target),
      icon: str(row.icon, '🦷'),
      active,
      spend,
      budget,
      leads,
      booked: num(row.booked),
      noShows: num(row.noShows ?? row.no_shows),
      revenue,
      costPerPatient,
      roi,
      health: paused ? 'paused' : campaignHealthToFe(row.health),
      alert: str(row.alert_message ?? row.alert, ''),
      alertType: campaignAlertTypeToFe(row.alert_type),
      lpPhase: campaignLpPhaseToFe(row.lp_phase),
    },
  };
}

function adaptCampaignHeaderStats(summary: BeCampaignSummary | undefined): ClHeaderStats {
  if (!summary) return emptyHeaderStats();

  const spendTile = tileByKey(summary.tiles, 'spend');

  const totalSpend =
    centsToDollars(summary.mom?.spend_cents?.current) ||
    centsToDollars(spendTile?.value_cents) ||
    centsToDollars(summary.total_spend_cents ?? summary.roi_banner?.spend_cents) ||
    0;

  const overallRoi = String(
    summary.roi_banner?.value ??
      summary.mom?.roi?.current ??
      summary.overall_roi ??
      summary.roi_banner?.multiple ??
      '0',
  );

  const spendDeltaLabel =
    formatMomDelta(summary.mom?.spend_cents?.delta, true) ??
    (spendTile?.delta != null ? String(spendTile.delta) : undefined);

  const roiDeltaLabel =
    summary.mom?.roi?.delta != null
      ? formatMomDelta(summary.mom.roi.delta, false)
      : tileByKey(summary.tiles, 'roi')?.delta != null
        ? String(tileByKey(summary.tiles, 'roi')?.delta)
        : undefined;

  return {
    totalSpend,
    totalLeads: num(summary.total_leads),
    totalBooked: num(summary.total_booked),
    totalNoShows: num(summary.total_no_shows),
    totalRevenue: centsToDollars(summary.total_revenue_cents ?? summary.roi_banner?.revenue_cents),
    avgCostPerPatient: centsToDollars(summary.avg_cost_per_patient_cents),
    overallRoi,
    noShowRate: num(summary.no_show_rate),
    ...(spendDeltaLabel ? { spendDeltaLabel } : {}),
    ...(roiDeltaLabel ? { roiDeltaLabel } : {}),
  };
}

function emptyHeaderStats(): ClHeaderStats {
  return {
    totalSpend: 0,
    totalLeads: 0,
    totalBooked: 0,
    totalNoShows: 0,
    totalRevenue: 0,
    avgCostPerPatient: 0,
    overallRoi: '0',
    noShowRate: 0,
  };
}

/** Location-level summary — `period` only; `campaign_id`/`health`/`period=july` are not valid on live. */
export async function fetchCampaignSummary(period?: string): Promise<ClHeaderStats> {
  try {
    const summary = await growthGet<BeCampaignSummary>('/campaigns/summary/', {
      period: period ?? defaultMarketingPeriod(),
    });
    return adaptCampaignHeaderStats(summary);
  } catch {
    return emptyHeaderStats();
  }
}

export async function fetchCampaignList(filters?: {
  period?: string;
  status?: string;
  page?: number;
  limit?: number;
}): Promise<CampaignListData> {
  const period = filters?.period ?? defaultMarketingPeriod();
  const listParams: Record<string, string | number> = {
    page: filters?.page ?? 1,
    limit: filters?.limit ?? 100,
  };
  if (filters?.status) listParams.status = filters.status;

  const [{ items: rows }, summary] = await Promise.all([
    growthList<BeCampaign>('/campaigns/', listParams),
    growthGet<BeCampaignSummary>('/campaigns/summary/', { period }).catch(() => undefined),
  ]);

  const items: ClCampaign[] = [];
  const idMap: Record<number, string> = {};
  for (const row of rows) {
    const { campaign, backendId } = adaptCampaign(row);
    items.push(campaign);
    idMap[campaign.id] = backendId;
  }

  return { items, idMap, headerStats: adaptCampaignHeaderStats(summary) };
}

export async function pauseCampaign(id: string) {
  return growthPost(`/campaigns/${id}/pause/`);
}

export async function resumeCampaign(id: string) {
  return growthPost(`/campaigns/${id}/resume/`);
}

export async function archiveCampaign(id: string) {
  return growthPost(`/campaigns/${id}/archive/`);
}

export async function patchCampaign(
  id: string,
  body: { monthly_budget_cents?: number; paused?: boolean },
) {
  return growthPatch(`/campaigns/${id}/`, body);
}

/** @deprecated Use `patchCampaign` — live ignores `active`; send `paused` instead. */
export async function patchCampaignBudget(id: string, budgetCents: number) {
  return patchCampaign(id, { monthly_budget_cents: budgetCents });
}
